/**
 * Shared vocabulary. These enums are the single source for database CHECK constraints,
 * API schemas and UI labels (see docs/PRODUCT.md §5). Change here first.
 */
import { z } from "zod";

export const ConversationStatus = z.enum([
  "new",
  "active",
  "waiting_contact",
  "waiting_internal",
  "snoozed",
  "closed",
]);
export type ConversationStatus = z.infer<typeof ConversationStatus>;

export const Handler = z.enum(["flow", "assistant", "human"]);
export type Handler = z.infer<typeof Handler>;

export const ChannelMode = z.enum(["flow", "assistant", "hybrid", "human"]);
export type ChannelMode = z.infer<typeof ChannelMode>;

export const ChannelKind = z.enum([
  "whatsapp",
  "instagram",
  "messenger",
  "rcs",
  "telegram",
  "email",
]);
export type ChannelKind = z.infer<typeof ChannelKind>;

export const MemberRole = z.enum([
  "owner",
  "admin",
  "manager",
  "supervisor",
  "operator",
  "auditor",
  "integration",
]);
export type MemberRole = z.infer<typeof MemberRole>;

export const Presence = z.enum(["online", "paused", "offline"]);
export type Presence = z.infer<typeof Presence>;

export const Priority = z.enum(["low", "normal", "high", "urgent"]);
export type Priority = z.infer<typeof Priority>;

export const SourceKind = z.enum([
  "inbound",
  "ad",
  "template",
  "campaign",
  "api",
  "flow",
  "connector",
  "reopen",
]);
export type SourceKind = z.infer<typeof SourceKind>;

export const OutcomeCategory = z.enum(["success", "neutral", "negative", "invalid"]);
export type OutcomeCategory = z.infer<typeof OutcomeCategory>;

export const MessageDirection = z.enum(["inbound", "outbound"]);
export const MessageType = z.enum([
  "text",
  "image",
  "audio",
  "video",
  "document",
  "sticker",
  "location",
  "contacts",
  "interactive",
  "template",
  "reaction",
  "system",
  "unsupported",
]);
export type MessageType = z.infer<typeof MessageType>;
export const MessageStatus = z.enum(["queued", "sent", "delivered", "read", "failed", "deleted"]);
export type MessageStatus = z.infer<typeof MessageStatus>;
export const AuthorKind = z.enum(["contact", "member", "assistant", "flow", "system", "api"]);
export type AuthorKind = z.infer<typeof AuthorKind>;
export const PricingCategory = z.enum([
  "marketing",
  "utility",
  "authentication",
  "service",
  "free",
]);
export type PricingCategory = z.infer<typeof PricingCategory>;

export const TemplateCategory = z.enum(["marketing", "utility", "authentication"]);
export const TemplateStatus = z.enum([
  "draft",
  "pending",
  "approved",
  "rejected",
  "paused",
  "disabled",
  "archived",
]);

export const GateMode = z.enum(["shadow", "log", "suggest", "auto_fix", "block"]);
export type GateMode = z.infer<typeof GateMode>;
export const GateVerdict = z.enum(["approve", "rewrite", "block"]);
export type GateVerdict = z.infer<typeof GateVerdict>;

export const ConsentPurpose = z.enum(["service", "utility", "marketing"]);
export const ConsentStatus = z.enum(["opted_in", "opted_out", "unknown"]);

export const FlowNodeKind = z.enum([
  "start",
  "message",
  "menu",
  "input",
  "condition",
  "http_request",
  "script",
  "set_variable",
  "set_field",
  "tag",
  "wait",
  "schedule",
  "business_hours",
  "transfer",
  "assistant",
  "close",
  "event",
  "exception",
  "end",
]);
export type FlowNodeKind = z.infer<typeof FlowNodeKind>;

export const Locale = z.enum(["pt-BR", "es", "en"]);
export type Locale = z.infer<typeof Locale>;

/** Translatable label owned by the customer. pt-BR is mandatory. */
export const LocalizedText = z.object({
  "pt-BR": z.string().min(1),
  es: z.string().optional(),
  en: z.string().optional(),
});
export type LocalizedText = z.infer<typeof LocalizedText>;
